// src/pages/BlogDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiCalendar, FiClock, FiUser, FiArrowLeft, FiShare2,
  FiTwitter, FiLinkedin, FiFacebook, FiCopy, FiCheck, FiTag, FiBookOpen
} from 'react-icons/fi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

import WhatsAppButton from '../components/WhatsAppButton';
import HomeBrandBanner from '../components/HomeBrandBanner';
import { getBlogById, mockBlogs } from '../data/blogsData';
import { useSiteContent } from '../context/SiteContentContext';

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { blogs } = useSiteContent();
  const [copied, setCopied] = useState(false);

  // Find blog from live MySQL DB context or fallback mock data
  const blogList = blogs && blogs.length > 0 ? blogs : mockBlogs;
  const blog = blogList.find((b) => String(b.id) === String(id)) || getBlogById(id);

  // Scroll to top whenever ID changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Get related blogs (excluding current)
  const relatedBlogs = blogList
    .filter((b) => String(b.id) !== String(blog.id))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-transparent relative overflow-hidden text-white select-none">
      <Navbar />

      {/* ─── HERO SECTION WITH SMOKE BACKGROUND ────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center pt-36 pb-24 overflow-hidden">
        {/* Background Smoke Video Overlay */}
        <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
          <video autoPlay loop muted playsInline className="object-cover w-full h-full opacity-100">
            <source src="/herovideo.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/75" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#000000] to-transparent z-10" />
        </div>

        <div className="relative z-20 max-w-5xl mx-auto px-6 w-full text-center">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-2 text-xs sm:text-sm text-white/60 mb-6 flex-wrap"
          >
            <Link to="/" className="hover:text-[#F2B135] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-[#F2B135] transition-colors">Blogs</Link>
            <span>/</span>
            <span className="text-[#F2B135] font-semibold truncate max-w-[200px] sm:max-w-[300px]">
              {blog.title}
            </span>
          </motion.div>

          {/* Category Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 bg-[#F2B135]/15 border border-[#F2B135]/40 px-4 py-1.5 rounded-full mb-6 backdrop-blur-md"
          >
            <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
            <span className="text-xs font-black text-[#F2B135] uppercase tracking-widest font-display">
              {blog.category || 'Tech Insights'}
            </span>
          </motion.div>

          {/* Blog Title in Hero */}
          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-8 font-display"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {blog.title}
          </motion.h1>

          {/* Meta Info Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex items-center justify-center gap-6 text-white/70 text-xs sm:text-sm font-medium flex-wrap bg-white/[0.03] border border-white/10 rounded-full py-3 px-6 max-w-2xl mx-auto backdrop-blur-md"
          >
            <div className="flex items-center gap-2">
              <img
                src="/favicon.png"
                alt="Rojrztech"
                className="w-5 h-5 rounded-full object-contain border border-[#F2B135]/50"
              />
              <span className="text-white font-bold">{blog.author || 'Rojrztech'}</span>
            </div>
            <span className="text-white/30">•</span>
            <div className="flex items-center gap-1.5">
              <FiCalendar className="text-[#F2B135]" />
              <span>{blog.date}</span>
            </div>
            <span className="text-white/30">•</span>
            <div className="flex items-center gap-1.5">
              <FiClock className="text-[#F2B135]" />
              <span>{blog.readTime}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── MAIN BLOG CONTENT SECTION ─────────────────────────────────── */}
      <section className="relative z-20 pt-8 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        
        {/* Back Button */}
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#F2B135] text-sm font-semibold mb-8 transition-colors duration-200 group"
        >
          <FiArrowLeft className="group-hover:-translate-x-1 transition-transform duration-200" />
          Back to all blogs
        </button>

        {/* Featured Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative aspect-[16/9] w-full rounded-2xl overflow-hidden border border-white/15 shadow-[0_20px_50px_rgba(0,0,0,0.8)] mb-12 group"
        >
          <img
            src={blog.image}
            alt={blog.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </motion.div>

        {/* Article Body */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="prose prose-invert max-w-none space-y-8"
        >
          {/* Excerpt Lead Paragraph */}
          <div className="text-lg sm:text-xl font-medium text-white/90 leading-relaxed border-l-4 border-[#F2B135] pl-6 py-1 bg-white/[0.02] rounded-r-xl">
            {blog.excerpt}
          </div>

          {/* Content Items Rendering */}
          {blog.content && blog.content.map((item, idx) => {
            if (item.type === "paragraph") {
              return (
                <p key={idx} className="text-white/80 text-base sm:text-lg leading-relaxed font-normal">
                  {item.text}
                </p>
              );
            }
            if (item.type === "heading") {
              return (
                <h2
                  key={idx}
                  className="text-2xl sm:text-3xl font-black text-white mt-10 mb-4 font-display flex items-center gap-3 pt-6 border-t border-white/10"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  <span className="w-2.5 h-7 rounded-full bg-[#F2B135] inline-block" />
                  {item.text}
                </h2>
              );
            }
            if (item.type === "quote") {
              return (
                <blockquote
                  key={idx}
                  className="my-8 p-6 rounded-2xl bg-gradient-to-r from-[#F2B135]/15 to-transparent border-l-4 border-[#F2B135] text-white/95 text-lg font-medium italic shadow-lg"
                >
                  "{item.text}"
                </blockquote>
              );
            }
            if (item.type === "keyTakeaways") {
              return (
                <div key={idx} className="my-8 p-6 sm:p-8 rounded-2xl bg-white/[0.03] border border-[#F2B135]/30 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#F2B135]/10 rounded-full blur-2xl pointer-events-none" />
                  <h3 className="text-xl font-bold text-[#F2B135] mb-4 flex items-center gap-2 font-display">
                    <FiBookOpen size={20} />
                    {item.title}
                  </h3>
                  <ul className="space-y-3">
                    {item.items.map((takeaway, tIdx) => (
                      <li key={tIdx} className="flex items-start gap-3 text-white/80 text-sm sm:text-base">
                        <span className="w-2 h-2 rounded-full bg-[#F2B135] mt-2 flex-shrink-0" />
                        <span>{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
            return null;
          })}

          {/* Standard Additional Content if content array is short */}
          {(!blog.content || blog.content.length <= 2) && (
            <div className="space-y-6 text-white/80 text-base sm:text-lg leading-relaxed">
              <p>
                At Rojrztech, our engineering and digital marketing teams bring years of enterprise experience building cutting-edge software solutions tailored for scalable business growth. Whether you are aiming to modernize your core IT infrastructure, deploy AI workflows, or enhance your brand's digital presence, having a clear technology roadmap is crucial.
              </p>
              
              <h2 className="text-2xl sm:text-3xl font-black text-white mt-10 mb-4 font-display flex items-center gap-3 pt-6 border-t border-white/10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="w-2.5 h-7 rounded-full bg-[#F2B135] inline-block" />
                Strategic Execution & Future Outlook
              </h2>
              
              <p>
                Success in today's rapidly changing tech ecosystem depends on continuous iteration and adopting modern industry standards. From bulletproof cloud architectures to high-converting UI design patterns, investing in quality software engineering delivers compounding long-term returns.
              </p>

              <blockquote className="my-8 p-6 rounded-2xl bg-gradient-to-r from-[#F2B135]/15 to-transparent border-l-4 border-[#F2B135] text-white/95 text-lg font-medium italic shadow-lg">
                "Innovation distinguishes between a leader and a follower. Delivering excellence in every line of code is how we help businesses transform."
              </blockquote>
            </div>
          )}
        </motion.article>

        {/* Tags & Share Bar */}
        <div className="mt-12 pt-8 border-t border-white/15 flex flex-col sm:flex-row items-center justify-between gap-6">
          {/* Tags */}
          <div className="flex items-center gap-2 flex-wrap">
            <FiTag className="text-[#F2B135]" />
            <span className="text-xs font-bold text-white/50 uppercase tracking-wider mr-1">Tags:</span>
            {(blog.tags || ["Technology", "Rojrztech", "Insights"]).map((tag, idx) => (
              <span
                key={idx}
                className="text-xs font-semibold px-3 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/80 hover:border-[#F2B135] hover:text-[#F2B135] transition-colors cursor-pointer"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Social Share Buttons */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-white/50 uppercase tracking-wider mr-1 flex items-center gap-1.5">
              <FiShare2 className="text-[#F2B135]" /> Share:
            </span>
            <button
              onClick={handleCopyLink}
              className="p-2.5 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#F2B135] hover:bg-[#F2B135]/20 text-white hover:text-[#F2B135] transition-all relative"
              title="Copy Link"
            >
              {copied ? <FiCheck className="text-green-400" /> : <FiCopy />}
            </button>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#F2B135] hover:bg-[#F2B135]/20 text-white hover:text-[#F2B135] transition-all"
              title="Share on Twitter"
            >
              <FiTwitter />
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#F2B135] hover:bg-[#F2B135]/20 text-white hover:text-[#F2B135] transition-all"
              title="Share on LinkedIn"
            >
              <FiLinkedin />
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#F2B135] hover:bg-[#F2B135]/20 text-white hover:text-[#F2B135] transition-all"
              title="Share on Facebook"
            >
              <FiFacebook />
            </a>
          </div>
        </div>

        {/* ─── HOME BRAND BANNER (DARK THEME LINK TO HOME PAGE) ────────── */}
        <div className="mt-12">
          <HomeBrandBanner />
        </div>

      </section>

      {/* ─── RELATED ARTICLES SECTION ──────────────────────────────────── */}
      <section className="relative z-20 py-16 bg-white/[0.01] px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-12 text-center sm:text-left">
            <div>
              <span className="text-xs font-bold text-[#F2B135] uppercase tracking-widest block mb-2 font-display">
                Continue Reading
              </span>
              <h3 className="text-2xl sm:text-4xl font-black text-white font-display" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Related Blogs &amp; Articles
              </h3>
            </div>
            <Link
              to="/blog"
              className="text-[#F2B135] font-bold text-sm hover:underline flex items-center gap-1"
            >
              View All Articles →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedBlogs.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="group relative rounded-2xl bg-white/[0.02] border border-white/10 p-4 transition-all duration-500 hover:border-[#F2B135]/40 hover:bg-[#F2B135]/5 hover:-translate-y-2 flex flex-col justify-between overflow-hidden shadow-2xl block"
              >
                <div className="space-y-4">
                  <div className="relative aspect-[16/10] rounded-xl overflow-hidden border border-white/5">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>

                  <div className="flex items-center gap-4 text-white/50 text-[11px] font-semibold uppercase tracking-wider">
                    <div className="flex items-center gap-1.5">
                      <FiCalendar className="text-[#F2B135]" />
                      {post.date}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FiClock className="text-[#F2B135]" />
                      {post.readTime}
                    </div>
                  </div>

                  <h4
                    className="text-white text-base font-bold group-hover:text-[#F2B135] transition-colors duration-300 leading-snug line-clamp-2 font-display"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {post.title}
                  </h4>

                  <p className="text-white/60 text-xs leading-relaxed line-clamp-2">
                    {post.excerpt}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-white/[0.06] flex items-center justify-between">
                  <span className="text-xs text-[#F2B135] font-bold group-hover:underline">
                    Read Article
                  </span>
                  <div className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center text-white/40 group-hover:border-[#F2B135] group-hover:bg-[#F2B135] group-hover:text-black transition-all duration-300 text-xs">
                    ›
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BlogDetails;
